import { useCallback } from "react";
import { OutlookRecipient, OutlookLocation } from "@shared/types/resource";
import { usePlatform } from "@/pages/taskpane/shared/hooks";
import { ResourceEntity } from "@/pages/taskpane/features/resources/services/types/resourceEntity";
import { WebAddinPlatforms } from "@/pages/taskpane/shared/hooks/usePlatform";
import { useOutlookStore } from "../stores/outlookStore";
import { generateRecurrenceUtcOccurrences } from "../../utils/occurrence";
import { useAppConfigStore, useAppointmentStore, useResourceStore } from "@/pages/taskpane/shared/stores";
import { isValidEmailAddress } from "../../utils/validate";
import { useCreateTemp } from "@/pages/taskpane/features/resources/services/useCreateTemp";
import { getOrganizerFromAppointment, getOrganizerEmailFromAppointment } from "../utils";
import { saveMeetingType } from "@/pages/taskpane/features/resources/services/useSaveMeetingType";

declare const Office: any;

// Enum for Exchange Resource Types (matching the original logic)
export enum ExchangeResourceTypeEnum {
  Room = 0,
  Equipment = 1,
  Workspace = 2,
}

// Helper function to validate Office.js availability
const validateOfficeContext = (): boolean => {
  return !!(Office && Office.context && Office.context.mailbox && Office.context.mailbox.item);
};

export const useOutlookActions = () => {
  const { getCurrentPlatform, isSetSupported } = usePlatform();
  const { syncFromOutlook, startDateTimeUtc, endDateTimeUtc } = useAppointmentStore();
  const { organizer, recurringDates, startTime, endTime, iCalUId, customUid, organizerName, globalId, timeZone } = useOutlookStore();
  const {
    setAppointmentTime,
    setAppointmentTimeWithZone,
    setSubject,
    setLocation,
    setEnhancedLocation,
    setOrganizer,
    setRequiredAttendees,
    setOptionalAttendees,
    setTimeZone,
    setError,
    setRecurringDates,
    setIsNoEndDateRecurring,
    setIsEditingRecurringSeries,
    isReopenAppointment,
    setRecurrence,
  } = useOutlookStore();
  const { meetingLabel: meetingLabelSelected, selectedResource } = useResourceStore();
  const { meetingLabel } = useAppConfigStore();
  const { mutateAsync: createTemp } = useCreateTemp({});

  const handleCreateTemp = useCallback(
    async(resources: ResourceEntity[]) => {
      for (const r of resources) {
        if (r?.childResources?.length) {
          await createTemp({
            name: "",
            subject: "",
            organizerEmail: organizer || "",
            organizerName: organizerName || "",
            guid: "",
            ressourceId: r.id,
            resourceIds: r?.childResources?.map((item) => item.id).join(","),
            childResourcesEmail: r?.childResources?.map((item) => item.email),
            startDate: startDateTimeUtc || startTime?.toISOString() || "",
            endDate: endDateTimeUtc || endTime?.toISOString() || "",
            uid: customUid || "",
            isRecurring: recurringDates?.length > 0 ? 1 : 0,
            busyStatus: "",
            orderStatus: "",
            isFromWebAddin: true,
            status: "",
          });
        }
        // TODO: Hiện tại dùng cho rc cũ -> luôn save meeting type khi click chọn resource bất kỳ không phân biệt có phải vr không
        await saveMeetingType({
          uid: customUid || "",
          resourcesIds: ([...selectedResource, r]).map((item) => item.id).join(","),
          // originalResourceIds: selectedResource.map((item) => item.id).join(","),
          meetingType: meetingLabel?.meetingLabels?.find((item) => item.id === meetingLabelSelected)?.name || "",
          isRecurring: recurringDates?.length > 0 ? 1 : 0,
          isNewMeeting: !iCalUId, // TODO: check if the meeting is new or not
          timeZoneName: timeZone || "",
          organizerName: organizerName || "",
          organizerEmail: organizer || "",
          startDate: startDateTimeUtc || "",
          endDate: endDateTimeUtc || "",
          globalId: globalId || "",
          isSendMail: false, // TODO: check if the meeting is send mail or not
          subject: "",
        });
      }
    },
    [createTemp]
  );

  // Convert resources to EmailUser format for required attendees
  const convertToEmailUser = useCallback((resources: ResourceEntity[]): Office.EmailUser[] => {
    return resources.map((resource) => ({
      displayName: resource.name,
      emailAddress: resource.email,
    }));
  }, []);

  // Convert resources to LocationIdentifier format for enhanced location
  const convertToLocationIdentifier = useCallback((resources: ResourceEntity[]): Office.LocationIdentifier[] => {
    let locationIdentifiers: Office.LocationIdentifier[] = [];
    resources.forEach((resource) => {
      if (resource.childResources.length > 0) {
        resource.childResources.forEach((childResource) => {
          locationIdentifiers.push({
            id: childResource.email,
            type: Office.MailboxEnums.LocationType.Room,
          });
        });
      } else {
        locationIdentifiers.push({
          id: resource.email,
          type: Office.MailboxEnums.LocationType.Room,
        });
      }
    });
    return locationIdentifiers;
    // return (
    //   resources
    //     // .filter(
    //     //   (resource) =>
    //     //     resource.ResourceType === ExchangeResourceTypeEnum.Room ||
    //     //     resource.ResourceType === ExchangeResourceTypeEnum.Workspace
    //     // )
    //     .map((resource) => ({
    //       id: resource.email,
    //       type: Office.MailboxEnums.LocationType.Room,
    //     }))
    // );
  }, []);

  // Convert equipment resources to EmailUser format
  const convertEquipmentToEmailUser = useCallback((resources: ResourceEntity[]): Office.EmailUser[] => {
    return (
      resources
        // .filter((resource) => resource.ResourceType === ExchangeResourceTypeEnum.Equipment)
        .map((resource) => ({
          displayName: resource.name,
          emailAddress: resource.email,
        }))
    );
  }, []);

  // Internal function to set locations
  const setLocationsInternal = useCallback(async (locations: string[]): Promise<boolean> => {
    try {
      const item = Office.context.mailbox.item;
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        console.error("Not an appointment item");
        return false;
      }

      const appCompose = item as Office.AppointmentCompose;
      const locationsString = locations.join(";");

      await new Promise<void>((resolve, reject) => {
        appCompose.location.setAsync(locationsString, (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(`Failed to set location: ${result.error?.message}`));
          }
        });
      });

      return true;
    } catch (error) {
      console.error("Failed to set locations:", error);
      return false;
    }
  }, []);

  // Add resources to appointment
  const AddResources = useCallback(
    async (resources: ResourceEntity[], locations?: string[]): Promise<boolean> => {
      try {
        // console.log("🚀 AddResources called with:", { resources: resources?.length, locations: locations?.length });
        handleCreateTemp(resources);
        // Validate Office.js context
        if (!validateOfficeContext()) {
          console.error("❌ Office.js context not available");
          return false;
        }

        const item = Office.context.mailbox.item;
        if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
          console.error("❌ Not an appointment item", { itemType: item?.itemType });
          return false;
        }

        const appCompose = item as Office.AppointmentCompose;
        const currentPlatform = getCurrentPlatform();
        const isMailbox18Supported = !Office.context.requirements.isSetSupported("Mailbox", "1.8");
        const isPlatformMac = currentPlatform === WebAddinPlatforms.OutlookForMac;

        // console.log("📋 Platform info:", { currentPlatform, isMailbox18Supported, isPlatformMac });

        if (!resources || resources.length === 0) {
          console.warn("⚠️ No resources to add");
          return true;
        }

        // For older API versions, add all resources as enhanced locations
        if (!isMailbox18Supported) {
          // console.log("📤 Using legacy approach (Mailbox < 1.8) with enhancedLocation");
          const locationIdentifiers = convertToLocationIdentifier(resources);

          if (locationIdentifiers.length > 0) {
            await new Promise<void>((resolve, reject) => {
              appCompose.enhancedLocation.addAsync(locationIdentifiers, (result: Office.AsyncResult<void>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  // console.log("✅ Successfully added enhanced locations:", locationIdentifiers.length);
                  resolve();
                } else {
                  console.error("❌ Failed to add enhanced locations:", result.error);
                  reject(new Error(`Failed to add enhanced locations: ${result.error?.message}`));
                }
              });
            });
          }

          // Set location for older versions
          if (locations && locations.length > 0) {
            await setLocationsInternal(locations);
          }
        } else {
          // For newer versions (Mailbox 1.8+), handle equipment and rooms separately
          // console.log("📤 Using modern approach (Mailbox 1.8+)");

          // Add equipment as required attendees
          const equipmentUsers = convertEquipmentToEmailUser(resources);
          if (equipmentUsers.length > 0) {
            await new Promise<void>((resolve, reject) => {
              appCompose.requiredAttendees.addAsync(equipmentUsers, (result: Office.AsyncResult<void>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  // console.log("✅ Successfully added equipment:", equipmentUsers.length);
                  resolve();
                } else {
                  console.error("❌ Failed to add equipment:", result.error);
                  reject(new Error(`Failed to add equipment: ${result.error?.message}`));
                }
              });
            });
          }

          // Add rooms/workspaces as enhanced locations (not supported on Mac)
          if (!isPlatformMac) {
            const locationIdentifiers = convertToLocationIdentifier(resources);
            if (locationIdentifiers.length > 0) {
              await new Promise<void>((resolve, reject) => {
                appCompose.enhancedLocation.addAsync(locationIdentifiers, (result: Office.AsyncResult<void>) => {
                  if (result.status === Office.AsyncResultStatus.Succeeded) {
                    // console.log("✅ Successfully added enhanced locations:", locationIdentifiers.length);
                    resolve();
                  } else {
                    console.error("❌ Failed to add enhanced locations:", result.error);
                    // Don't reject for enhanced locations on unsupported platforms
                    if (result.error?.code === 5001) {
                      console.warn("⚠️ Enhanced locations not supported on this platform");
                      resolve();
                    } else {
                      reject(new Error(`Failed to add enhanced locations: ${result.error?.message}`));
                    }
                  }
                });
              });
            }
          }

          // Set basic location as fallback or for Mac
          if (locations && locations.length > 0) {
            await setLocationsInternal(locations);
          }
        }

        // console.log("✅ AddResources completed successfully");
        return true;
      } catch (error) {
        console.error("❌ Failed to add resources:", error);
        return false;
      }
    },
    [
      getCurrentPlatform,
      isSetSupported,
      convertToEmailUser,
      convertToLocationIdentifier,
      convertEquipmentToEmailUser,
      setLocationsInternal,
    ]
  );

  const UpdateAttendees = useCallback(
    async (
      attendeesToAdd: { Email: string; Name: string; IsRequired?: boolean }[],
      attendeesToRemove: { Email: string; Name: string; IsRequired?: boolean }[]
    ): Promise<void> => {
      try {
        const item = Office.context.mailbox.item;
        if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
          console.error("Not an appointment item");
          return;
        }
        const appCompose = item as Office.AppointmentCompose;
        const currentRequiredAttendees = await new Promise<Office.EmailAddressDetails[]>((resolve, reject) => {
          appCompose.requiredAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve(result.value || []);
            } else {
              console.error("❌ Failed to get required attendees:", result.error);
              reject(new Error(`Failed to get required attendees: ${result.error?.message}`));
            }
          });
        });

        // Get current optional attendees
        const currentOptionalAttendees = await new Promise<Office.EmailAddressDetails[]>((resolve, reject) => {
          appCompose.optionalAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve(result.value || []);
            } else {
              console.error("❌ Failed to get optional attendees:", result.error);
              reject(new Error(`Failed to get optional attendees: ${result.error?.message}`));
            }
          });
        });
        const remainingRequiredAttendees = currentRequiredAttendees.filter(
          (attendee) => !attendeesToRemove.some((a) => a.Email.toLowerCase() === attendee.emailAddress.toLowerCase())
        );
        const remainingOptionalAttendees = currentOptionalAttendees.filter(
          (attendee) => !attendeesToRemove.some((a) => a.Email.toLowerCase() === attendee.emailAddress.toLowerCase())
        );
        if (attendeesToAdd.length > 0) {
          attendeesToAdd.forEach((attendee) => {
            if (attendee.IsRequired) {
              remainingRequiredAttendees.push({
                displayName: attendee.Name,
                emailAddress: attendee.Email,
                appointmentResponse: Office.MailboxEnums.ResponseType.None,
                recipientType: Office.MailboxEnums.RecipientType.Other,
              });
            } else {
              remainingOptionalAttendees.push({
                displayName: attendee.Name,
                emailAddress: attendee.Email,
                appointmentResponse: Office.MailboxEnums.ResponseType.None,
                recipientType: Office.MailboxEnums.RecipientType.Other,
              });
            }
          });
        }
        appCompose.requiredAttendees.setAsync(remainingRequiredAttendees, (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            return true;
          } else {
            console.error("❌ Failed to update required attendees:", result.error);
            return false;
          }
        });
        appCompose.optionalAttendees.setAsync(remainingOptionalAttendees, (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            return true;
          } else {
            console.error("❌ Failed to update optional attendees:", result.error);
            return false;
          }
        });
        return;
      } catch (error) {
        console.error("❌ Failed to update attendees:", error);
        return;
      }
    },
    []
  );

  // Remove resources from appointment
  const RemoveResources = useCallback(
    async (resources: ResourceEntity[]): Promise<boolean> => {
      try {
        console.log("🗑️ RemoveResources called with:", { resources: resources });

        // Validate Office.js context
        if (!validateOfficeContext()) {
          console.error("❌ Office.js context not available");
          return false;
        }

        const item = Office.context.mailbox.item;
        if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
          console.error("❌ Not an appointment item", { itemType: item?.itemType });
          return false;
        }

        const appCompose = item as Office.AppointmentCompose;
        const currentPlatform = getCurrentPlatform();
        const isMailbox18Supported = !Office.context.requirements.isSetSupported("Mailbox", "1.8");
        const isPlatformMac = currentPlatform === WebAddinPlatforms.OutlookForMac;

        // console.log("📋 Platform info:", { currentPlatform, isMailbox18Supported, isPlatformMac });

        if (!resources || resources.length === 0) {
          console.warn("⚠️ No resources to remove");
          return true;
        }

        // Get organizer email to exclude from removal
        let organizerEmail = "";
        try {
          organizerEmail = await getOrganizerEmailFromAppointment(appCompose);
        } catch (error) {
          console.warn("⚠️ Could not get organizer email, using user profile:", error);
          organizerEmail = Office.context.mailbox.userProfile.emailAddress?.toLowerCase() || "";
        }

        // Get current required attendees
        const currentRequiredAttendees = await new Promise<Office.EmailAddressDetails[]>((resolve, reject) => {
          appCompose.requiredAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve(result.value || []);
            } else {
              console.error("❌ Failed to get required attendees:", result.error);
              reject(new Error(`Failed to get required attendees: ${result.error?.message}`));
            }
          });
        });

        // Get current optional attendees
        const currentOptionalAttendees = await new Promise<Office.EmailAddressDetails[]>((resolve, reject) => {
          appCompose.optionalAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve(result.value || []);
            } else {
              console.error("❌ Failed to get optional attendees:", result.error);
              reject(new Error(`Failed to get optional attendees: ${result.error?.message}`));
            }
          });
        });

        // Filter out resources to remove (excluding organizer)
        const listResourceEmails: string[] = [];
        resources.forEach((r) => {
          if (r?.childResources?.length) {
            r.childResources.forEach((c) => {
              listResourceEmails.push(c.email.toLowerCase());
            });
          } else if (r?.email) {
            listResourceEmails.push(r.email.toLowerCase());
          }
        });
        // const emailsToRemove = new Set(resources.map((r) => r.email.toLowerCase()));
        const emailsToRemove = new Set(listResourceEmails);
        const remainingRequiredAttendees = currentRequiredAttendees.filter((attendee) => {
          const email = attendee.emailAddress.toLowerCase();
          const shouldKeep = email !== organizerEmail.toLowerCase() && !emailsToRemove.has(email);
          return shouldKeep;
        });

        const remainingOptionalAttendees = currentOptionalAttendees.filter((attendee) => {
          const email = attendee.emailAddress.toLowerCase();
          const shouldKeep = email !== organizerEmail.toLowerCase() && !emailsToRemove.has(email);
          return shouldKeep;
        });

        // Convert to EmailUser format for setAsync
        const requiredEmailUsers: Office.EmailUser[] = remainingRequiredAttendees.map((attendee) => ({
          displayName: attendee.displayName,
          emailAddress: attendee.emailAddress,
        }));

        const optionalEmailUsers: Office.EmailUser[] = remainingOptionalAttendees.map((attendee) => ({
          displayName: attendee.displayName,
          emailAddress: attendee.emailAddress,
        }));

        // Update required attendees
        await new Promise<void>((resolve, reject) => {
          appCompose.requiredAttendees.setAsync(requiredEmailUsers, (result: Office.AsyncResult<void>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve();
            } else {
              console.error("❌ Failed to update required attendees:", result.error);
              reject(new Error(`Failed to update required attendees: ${result.error?.message}`));
            }
          });
        });

        // Update optional attendees
        await new Promise<void>((resolve, reject) => {
          appCompose.optionalAttendees.setAsync(optionalEmailUsers, (result: Office.AsyncResult<void>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve();
            } else {
              console.error("❌ Failed to update optional attendees:", result.error);
              reject(new Error(`Failed to update optional attendees: ${result.error?.message}`));
            }
          });
        });

        // Handle enhanced location removal for all versions (except Mac for modern API)
        // Filter only Room resources for enhanced location removal

        if (resources.length > 0) {
          // Get current enhanced locations from Outlook to match exact locationIdentifier.id
          // This is critical because Outlook may store id as email or displayName
          let currentEnhancedLocations: Office.LocationDetails[] = [];
          if ((!isMailbox18Supported || (!isPlatformMac && isMailbox18Supported)) && !isPlatformMac) {
            try {
              currentEnhancedLocations = await new Promise<Office.LocationDetails[]>((resolve, reject) => {
                appCompose.enhancedLocation.getAsync((result: Office.AsyncResult<Office.LocationDetails[]>) => {
                  if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(result.value || []);
                  } else {
                    console.warn("⚠️ Failed to get enhanced locations for removal:", result.error);
                    resolve([]);
                  }
                });
              });
            } catch (error) {
              console.warn("⚠️ Error getting enhanced locations:", error);
              currentEnhancedLocations = [];
            }
          }

          // Build locationIdentifiers by matching resources with current enhanced locations
          // Match by email to find the exact locationIdentifier.id stored in Outlook
          const locationIdentifiersToRemove: Office.LocationIdentifier[] = [];
          const resourceEmails = new Set(resources.map((r) => r.email.toLowerCase()));

          currentEnhancedLocations.forEach((location) => {
            // Get email from location (could be in emailAddress, locationIdentifier.id, or displayName)
            const locationEmail = (location.emailAddress || "").toLowerCase();
            const locationId = (location.locationIdentifier?.id || "").toLowerCase();
            const locationDisplayName = (location.displayName || "").toLowerCase();

            // Check if this location matches any resource we want to remove
            listResourceEmails.forEach((email) => {
              // const resourceEmail = resource.email.toLowerCase();
              // const resourceName = (resource.name || "").toLowerCase();

              // Match by email (check all possible fields)
              const isMatch = email === locationEmail || email === locationId || email === locationDisplayName;
              // ||
              // resourceName === locationEmail ||
              // resourceName === locationId ||
              // resourceName === locationDisplayName;

              if (isMatch) {
                // Use the exact locationIdentifier.id that Outlook has stored
                // This is critical - Outlook may store id as email or displayName
                if (location.locationIdentifier?.id) {
                  locationIdentifiersToRemove.push({
                    id: location.locationIdentifier.id,
                    type: location.locationIdentifier.type || Office.MailboxEnums.LocationType.Room,
                  });
                } else {
                  // Fallback: use emailAddress or displayName
                  const fallbackId = location.emailAddress || location.displayName || email;
                  locationIdentifiersToRemove.push({
                    id: fallbackId,
                    type: Office.MailboxEnums.LocationType.Room,
                  });
                }
              }
            });
          });

          // If no matches found, try using email and name as fallback (for resources added by our add-in)
          if (locationIdentifiersToRemove.length === 0) {
            // console.log("⚠️ No matching enhanced locations found, trying email and name as fallback");
            resources.forEach((resource) => {
              // Try both email and name as id (Outlook might have stored either)
              locationIdentifiersToRemove.push({
                id: resource.email,
                type: Office.MailboxEnums.LocationType.Room,
              });
              // Also try name if it's different from email
              if (resource.name && resource.name.toLowerCase() !== resource.email.toLowerCase()) {
                locationIdentifiersToRemove.push({
                  id: resource.name,
                  type: Office.MailboxEnums.LocationType.Room,
                });
              }
            });
          }

          // console.log("🗑️ Removing enhanced locations:", {
          //   roomResources: roomResources.map((r) => ({ email: r.email, name: r.name })),
          //   currentLocations: currentEnhancedLocations.map((l) => ({
          //     emailAddress: l.emailAddress,
          //     displayName: l.displayName,
          //     locationIdentifierId: l.locationIdentifier?.id,
          //   })),
          //   locationIdentifiersToRemove: locationIdentifiersToRemove,
          // });

          if (locationIdentifiersToRemove.length > 0) {
            if (!isMailbox18Supported) {
              // Legacy API: Remove from enhanced locations
              await new Promise<void>((resolve, reject) => {
                appCompose.enhancedLocation.removeAsync(
                  locationIdentifiersToRemove,
                  (result: Office.AsyncResult<void>) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                      // console.log("✅ Successfully removed enhanced locations (legacy)", locationIdentifiersToRemove.length);
                      resolve();
                    } else {
                      console.error("❌ Failed to remove enhanced locations (legacy):", result.error);
                      if (result.error?.code === 5001) {
                        console.warn("⚠️ Enhanced locations removal not supported on this platform");
                      }
                      resolve();
                    }
                  }
                );
              });
            } else {
              // Modern API: Remove from enhanced locations (not supported on Mac)
              if (!isPlatformMac) {
                await new Promise<void>((resolve, reject) => {
                  appCompose.enhancedLocation.removeAsync(
                    locationIdentifiersToRemove,
                    (result: Office.AsyncResult<void>) => {
                      if (result.status === Office.AsyncResultStatus.Succeeded) {
                        // console.log("✅ Successfully removed enhanced locations (modern)", locationIdentifiersToRemove.length);
                        resolve();
                      } else {
                        console.error("❌ Failed to remove enhanced locations (modern):", result.error);
                        if (result.error?.code === 5001) {
                          console.warn("⚠️ Enhanced locations removal not supported on this platform");
                        }
                        resolve();
                      }
                    }
                  );
                });
              }
            }
          }
        }

        // For Mac, clear basic location if rooms were removed
        if (isPlatformMac) {
          const roomResources = resources.filter((r) => r.resourceType === "Rooms");
          if (roomResources.length > 0) {
            // console.log("📍 Clearing basic location for removed rooms (Mac)");
            await setLocationsInternal([]); // Clear location
          }
        }

        // console.log("✅ RemoveResources completed successfully");
        return true;
      } catch (error) {
        console.error("❌ Failed to remove resources:", error);
        return false;
      }
    },
    [getCurrentPlatform, isSetSupported, convertToLocationIdentifier, setLocationsInternal]
  );

  // Update recipients (both required and optional)
  const UpdateRecipients = useCallback(async (recipients: OutlookRecipient[]): Promise<boolean> => {
    try {
      const item = Office.context.mailbox.item;
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        console.error("Not an appointment item");
        return false;
      }

      const appCompose = item as Office.AppointmentCompose;

      // Get organizer from appointment item (supports shared mailbox)
      const organizerEmail = await getOrganizerEmailFromAppointment(appCompose);

      // Separate required and optional recipients
      const requiredRecipients = recipients.filter((recipient) => recipient.Email.toLowerCase() !== organizerEmail);
      const optionalRecipients: OutlookRecipient[] = []; // Could be extended based on business logic

      // Convert to Office format
      const requiredEmailDetails: Office.EmailAddressDetails[] = requiredRecipients.map((recipient) => ({
        displayName: recipient.Name,
        emailAddress: recipient.Email,
        appointmentResponse: Office.MailboxEnums.ResponseType.None,
        recipientType: Office.MailboxEnums.RecipientType.Other,
      }));

      const optionalEmailUsers: Office.EmailUser[] = optionalRecipients.map((recipient) => ({
        displayName: recipient.Name,
        emailAddress: recipient.Email,
      }));

      // Update required attendees
      await new Promise<void>((resolve, reject) => {
        appCompose.requiredAttendees.setAsync(requiredEmailDetails, (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(`Failed to update required attendees: ${result.error?.message}`));
          }
        });
      });

      // Update optional attendees
      await new Promise<void>((resolve, reject) => {
        appCompose.optionalAttendees.setAsync(optionalEmailUsers, (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(`Failed to update optional attendees: ${result.error?.message}`));
          }
        });
      });

      return true;
    } catch (error) {
      console.error("Failed to update recipients:", error);
      return false;
    }
  }, []);

  // Set start and end time of the appointment
  const SetAppointmentTime = useCallback(
    async (start: Date, end: Date): Promise<boolean> => {
      try {
        // Validate context
        if (!validateOfficeContext()) {
          console.error("❌ Office.js context not available");
          return false;
        }

        const item = Office.context.mailbox.item;
        if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
          console.error("❌ Not an appointment item", { itemType: item?.itemType });
          return false;
        }

        const appCompose = item as Office.AppointmentCompose;

        // Update start & end in parallel
        await Promise.all([
          new Promise<void>((resolve, reject) => {
            appCompose.start.setAsync(start, (result: Office.AsyncResult<void>) => {
              result.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(result.error);
            });
          }),
          new Promise<void>((resolve, reject) => {
            appCompose.end.setAsync(end, (result: Office.AsyncResult<void>) => {
              result.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(result.error);
            });
          }),
        ]);

        // Convert to local client time
        let startDateTimeZone = null;
        let endDateTimeZone = null;

        try {
          if (start && Office?.context?.mailbox?.convertToLocalClientTime) {
            startDateTimeZone = Office.context.mailbox.convertToLocalClientTime(start);
            console.log("🌐 Converted start time to local:", startDateTimeZone);
          }

          if (end && Office?.context?.mailbox?.convertToLocalClientTime) {
            endDateTimeZone = Office.context.mailbox.convertToLocalClientTime(end);
            console.log("🌐 Converted end time to local:", endDateTimeZone);
          }
        } catch (error) {
          console.warn("⚠️ Failed to convert to local client time:", error);
        }

        // Sync to store with timezone
        setAppointmentTimeWithZone(start, end, startDateTimeZone, endDateTimeZone);

        console.log("✅ SetAppointmentTime completed", { start, end, startDateTimeZone, endDateTimeZone });
        return true;
      } catch (error) {
        console.error("❌ Failed to set appointment time:", error);
        return false;
      }

      return true;
    },
    [setAppointmentTimeWithZone]
  );

  // Set locations
  const SetLocations = useCallback(
    async (locations: string[]): Promise<boolean> => {
      return setLocationsInternal(locations);
    },
    [setLocationsInternal]
  );

  // Get current recipients
  const GetRecipients = useCallback(async (): Promise<OutlookRecipient[]> => {
    try {
      // console.log("📋 GetRecipients called");

      // Validate Office.js context
      if (!validateOfficeContext()) {
        console.error("❌ Office.js context not available");
        return [];
      }

      const item = Office.context.mailbox.item;
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        console.error("❌ Not an appointment item", { itemType: item?.itemType });
        return [];
      }

      const appCompose = item as Office.AppointmentCompose;
      const currentPlatform = getCurrentPlatform();
      const isMailbox18Supported = !Office.context.requirements.isSetSupported("Mailbox", "1.8");
      const isPlatformMac = currentPlatform === WebAddinPlatforms.OutlookForMac;

      // console.log("📋 Platform info:", { currentPlatform, isMailbox18Supported, isPlatformMac });

      // Get organizer email from appointment item (supports shared mailbox)
      let organizerEmail = "";
      try {
        organizerEmail = await getOrganizerEmailFromAppointment(appCompose);
      } catch (error) {
        console.warn("⚠️ Could not get organizer email:", error);
        organizerEmail = Office.context.mailbox.userProfile.emailAddress?.toLowerCase() || "";
      }

      // Get required attendees
      const requiredAttendees = await new Promise<Office.EmailAddressDetails[]>((resolve, reject) => {
        appCompose.requiredAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            // console.log("📋 Required attendees:", result.value?.length);
            resolve(result.value || []);
          } else {
            console.error("❌ Failed to get required attendees:", result.error);
            resolve([]); // Don't reject, just return empty array
          }
        });
      });

      // Get optional attendees
      const optionalAttendees = await new Promise<Office.EmailAddressDetails[]>((resolve, reject) => {
        appCompose.optionalAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            // console.log("📋 Optional attendees:", result.value?.length);
            resolve(result.value || []);
          } else {
            console.error("❌ Failed to get optional attendees:", result.error);
            resolve([]); // Don't reject, just return empty array
          }
        });
      });

      // Get enhanced locations (if supported and not Mac)
      let enhancedLocations: Office.LocationDetails[] = [];
      if (isMailbox18Supported && !isPlatformMac) {
        try {
          enhancedLocations = await new Promise<Office.LocationDetails[]>((resolve, reject) => {
            appCompose.enhancedLocation.getAsync((result: Office.AsyncResult<Office.LocationDetails[]>) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                // console.log("📋 Enhanced locations:", result.value?.length);
                resolve(result.value || []);
              } else {
                console.warn("⚠️ Failed to get enhanced locations:", result.error);
                resolve([]); // Don't reject, just return empty array
              }
            });
          });
        } catch (error) {
          console.warn("⚠️ Failed to get enhanced locations:", error);
        }
      }

      // Combine all recipients, excluding organizer
      const allRecipients: OutlookRecipient[] = [];

      // Add required attendees (excluding organizer)
      requiredAttendees.forEach((attendee) => {
        if (attendee.emailAddress.toLowerCase() !== organizerEmail.toLowerCase()) {
          allRecipients.push({
            Name: attendee.displayName || attendee.emailAddress,
            Email: attendee.emailAddress || attendee.displayName,
            RecipientType: ExchangeResourceTypeEnum.Equipment, // Default to equipment for attendees
          });
        }
      });

      // Add optional attendees (excluding organizer)
      optionalAttendees.forEach((attendee) => {
        if (attendee.emailAddress.toLowerCase() !== organizerEmail.toLowerCase()) {
          allRecipients.push({
            Name: attendee.displayName || attendee.emailAddress,
            Email: attendee.emailAddress || attendee.displayName,
            RecipientType: ExchangeResourceTypeEnum.Equipment,
          });
        }
      });

      // Add enhanced locations as rooms
      enhancedLocations.forEach((location) => {
        const email = location.emailAddress || location.displayName || location.locationIdentifier?.id || "";
        if (email && email.toLowerCase() !== organizerEmail.toLowerCase()) {
          allRecipients.push({
            Name: location.displayName || email,
            Email: email,
            RecipientType: ExchangeResourceTypeEnum.Room,
          });
        }
      });

      // console.log("✅ GetRecipients completed:", { totalRecipients: allRecipients.length });
      return allRecipients;
    } catch (error) {
      console.error("❌ Failed to get recipients:", error);
      return [];
    }
  }, [getCurrentPlatform, isSetSupported]);

  // Initialize appointment data from Outlook and sync with store
  const InitializeAppointmentData = useCallback(async (): Promise<boolean> => {
    try {
      // console.log("🚀 InitializeAppointmentData called");

      // Validate Office.js context
      if (!validateOfficeContext()) {
        console.error("❌ Office.js context not available");
        setError("Office.js context not available");
        return false;
      }

      const item = Office.context.mailbox.item;
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        console.error("❌ Not an appointment item", { itemType: item?.itemType });
        setError("Not an appointment item");
        return false;
      }

      const appCompose = item as Office.AppointmentCompose;
      // console.log("📋 Starting appointment data initialization...");

      // Initialize all appointment data in parallel
      const initPromises = [
        // Get appointment times
        new Promise<void>((resolve) => {
          Promise.all([
            new Promise<Date>((resolveStart) => {
              appCompose.start.getAsync((result: Office.AsyncResult<Date>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  resolveStart(result.value);
                } else {
                  console.warn("⚠️ Failed to get start time:", result.error);
                  resolveStart(new Date());
                }
              });
            }),
            new Promise<Date>((resolveEnd) => {
              appCompose.end.getAsync((result: Office.AsyncResult<Date>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  resolveEnd(result.value);
                } else {
                  console.warn("⚠️ Failed to get end time:", result.error);
                  resolveEnd(new Date(Date.now() + 3600000)); // Default to 1 hour later
                }
              });
            }),
          ]).then(([startTime, endTime]) => {
            // console.log("📅 Setting appointment times:", { startTime, endTime });

            // Convert to local client time
            let startDateTimeZone = null;
            let endDateTimeZone = null;

            try {
              if (startTime && Office?.context?.mailbox?.convertToLocalClientTime) {
                startDateTimeZone = Office.context.mailbox.convertToLocalClientTime(startTime);
                // console.log("🌐 Converted start time to local:", startDateTimeZone);
              }

              if (endTime && Office?.context?.mailbox?.convertToLocalClientTime) {
                endDateTimeZone = Office.context.mailbox.convertToLocalClientTime(endTime);
                // console.log("🌐 Converted end time to local:", endDateTimeZone);
              }
            } catch (error) {
              console.warn("⚠️ Failed to convert to local client time:", error);
            }

            setAppointmentTimeWithZone(startTime, endTime, startDateTimeZone, endDateTimeZone);
            const userTimeZone =
              Office.context.mailbox.userProfile.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            syncFromOutlook(startTime, endTime, startDateTimeZone, endDateTimeZone, userTimeZone);
            resolve();
          });
        }),

        // Get subject
        new Promise<void>((resolve) => {
          appCompose.subject.getAsync((result: Office.AsyncResult<string>) => {
            const subject = result.status === Office.AsyncResultStatus.Succeeded ? result.value || "" : "";
            // console.log("📝 Setting subject:", subject);
            setSubject(subject);
            resolve();
          });
        }),

        // Get basic location
        new Promise<void>((resolve) => {
          appCompose.location.getAsync((result: Office.AsyncResult<string>) => {
            const locationString = result.status === Office.AsyncResultStatus.Succeeded ? result.value || "" : "";
            const locationArray: OutlookLocation[] = locationString
              ? [
                {
                  displayName: locationString,
                  emailAddress: locationString || "",
                  locationIdentifier: {
                    id: locationString,
                    type: "Custom",
                  },
                },
              ]
              : [];
            // console.log("📍 Setting location:", locationArray);
            setLocation(locationArray);
            resolve();
          });
        }),

        // Get organizer
        new Promise<void>((resolve) => {
          getOrganizerFromAppointment(appCompose).then(({ email: organizerEmail, name: organizerName }) => {
            // console.log("🎯 Setting organizer:", organizerEmail);
            setOrganizer(organizerEmail, organizerName);
            resolve();
          });
        }),

        // Get user timezone
        new Promise<void>((resolve) => {
          try {
            // Get timezone from Office context
            const userTimeZone =
              Office.context.mailbox.userProfile.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            // console.log("🌍 Setting user timezone:", userTimeZone);
            setTimeZone(userTimeZone);
            resolve();
          } catch (error) {
            console.warn("⚠️ Failed to get user timezone, using browser timezone:", error);
            // Fallback to browser timezone
            const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            // console.log("🌍 Setting fallback timezone:", browserTimeZone);
            setTimeZone(browserTimeZone);
            resolve();
          }
        }),

        // Get required attendees
        new Promise<void>((resolve) => {
          appCompose.requiredAttendees.getAsync(async (result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const organizerEmail = await getOrganizerEmailFromAppointment(appCompose);
              const attendees = (result.value || [])
                .filter((attendee) => {
                  // Filter out organizer and invalid email addresses
                  if (!attendee.emailAddress) return false;
                  if (attendee.emailAddress.toLowerCase() === organizerEmail) return false;
                  return isValidEmailAddress(attendee.emailAddress);
                })
                .map((attendee) => ({
                  Name: attendee.displayName || attendee.emailAddress || "",
                  Email: attendee.emailAddress || "",
                  RecipientType: ExchangeResourceTypeEnum.Equipment, // Default
                }));
              // console.log("👥 Setting required attendees:", attendees);
              setRequiredAttendees(attendees);
            } else {
              console.warn("⚠️ Failed to get required attendees:", result.error);
              setRequiredAttendees([]);
            }
            resolve();
          });
        }),

        // Get optional attendees
        new Promise<void>((resolve) => {
          appCompose.optionalAttendees.getAsync(async (result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const organizerEmail = await getOrganizerEmailFromAppointment(appCompose);
              const attendees = (result.value || [])
                .filter((attendee) => {
                  // Filter out organizer and invalid email addresses
                  if (!attendee.emailAddress) return false;
                  if (attendee.emailAddress.toLowerCase() === organizerEmail) return false;
                  return isValidEmailAddress(attendee.emailAddress);
                })
                .map((attendee) => ({
                  Name: attendee.displayName || attendee.emailAddress || "",
                  Email: attendee.emailAddress || "",
                  RecipientType: ExchangeResourceTypeEnum.Equipment, // Default
                }));
              // console.log("👤 Setting optional attendees:", attendees);
              setOptionalAttendees(attendees);
            } else {
              console.warn("⚠️ Failed to get optional attendees:", result.error);
              setOptionalAttendees([]);
            }
            resolve();
          });
        }),

        // Get recurrence data
        new Promise<void>((resolve) => {
          appCompose.recurrence.getAsync((result: Office.AsyncResult<Office.Recurrence>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const recurrence = result.value as any;
              setRecurrence(recurrence);
              const seriesId = appCompose.seriesId;
              if (recurrence && !seriesId) {
                try {
                  const recurringDates = generateRecurrenceUtcOccurrences(recurrence, 50);
                  setRecurringDates(recurringDates);
                } catch (error) {
                  console.warn("⚠️ Failed to get recurring dates:", error);
                  setRecurringDates([]);
                }
                setIsNoEndDateRecurring(
                  !Boolean(
                    recurrence?.seriesTime?.endDay &&
                    recurrence?.seriesTime?.endMonth &&
                    recurrence?.seriesTime?.endYear
                  )
                );

                // Nếu edit series thì recurrence.setAsync -> false
                if (isReopenAppointment) {
                  appCompose.recurrence.setAsync(recurrence, (setResult: Office.AsyncResult<void>) => {
                    if (setResult.status === Office.AsyncResultStatus.Succeeded) {
                      setIsEditingRecurringSeries(true);
                    } else {
                      setIsEditingRecurringSeries(false);
                    }
                  });
                } else {
                  setIsEditingRecurringSeries(false);
                }
              } else {
                setRecurringDates([]);
                setIsNoEndDateRecurring(false);
                setIsEditingRecurringSeries(false);
              }
            } else {
              setRecurringDates([]);
              setIsNoEndDateRecurring(false);
            }
            resolve();
          });
        }),
      ];

      // Get enhanced locations if supported
      const currentPlatform = getCurrentPlatform();
      const isMailbox18Supported = Office.context.requirements.isSetSupported("Mailbox", "1.8");
      const isPlatformMac = currentPlatform === WebAddinPlatforms.OutlookForMac;

      if (isMailbox18Supported && !isPlatformMac) {
        initPromises.push(
          new Promise<void>((resolve) => {
            appCompose.enhancedLocation.getAsync((result: Office.AsyncResult<Office.LocationDetails[]>) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                const locations = result.value || [];

                // console.log("🏢 Setting enhanced locations:", locations);
                const enhancedLocations = locations?.map((location) => {
                  return {
                    displayName: location.displayName,
                    emailAddress: location.emailAddress || location.locationIdentifier?.id || "",
                    locationIdentifier: {
                      id: location.locationIdentifier?.id,
                      type: location.locationIdentifier?.type,
                    },
                  };
                });
                // console.log("🏢 Enhanced locations:", enhancedLocations);
                setEnhancedLocation(enhancedLocations);
              } else {
                console.warn("⚠️ Failed to get enhanced locations:", result.error);
                setEnhancedLocation([]);
              }
              resolve();
            });
          })
        );
      }

      // Wait for all initialization to complete
      await Promise.all(initPromises);

      // console.log("✅ InitializeAppointmentData completed successfully");
      setError(null); // Clear any previous errors
      return true;
    } catch (error) {
      console.error("❌ Failed to initialize appointment data:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to initialize appointment data";
      setError(errorMessage);
      return false;
    }
  }, [
    getCurrentPlatform,
    isSetSupported,
    setAppointmentTime,
    setAppointmentTimeWithZone,
    setSubject,
    setLocation,
    setEnhancedLocation,
    setOrganizer,
    setRequiredAttendees,
    setOptionalAttendees,
    setTimeZone,
    setError,
    setRecurringDates,
    setIsNoEndDateRecurring,
    setIsEditingRecurringSeries,
    isReopenAppointment,
  ]);

  const saveSessionId = (key: string, sessionId: string): Promise<string> => {
    const item = Office.context.mailbox.item;
    if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
      return Promise.reject("Not an appointment item");
    }
    const appCompose = item as Office.AppointmentCompose;
    return new Promise((resolve) => {
      if (appCompose) {
        appCompose.sessionData.getAsync(key, (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
            if (asyncResult.value) {
              const customUid = asyncResult.value as string;
              if(customUid !== sessionId) {
                appCompose.sessionData.setAsync(key, sessionId, (asyncResult) => {
                  if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(sessionId);
                  } else {
                    resolve("");
                  }
                });
              }
              resolve(customUid);
            } else {
              resolve("");
            }
          } else if (appCompose) {
            appCompose.sessionData.setAsync(key, sessionId, (asyncResult) => {
              if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                resolve(sessionId);
              } else {
                resolve("");
              }
            });
          } else {
            resolve("");
          }
        });
      } else {
        resolve("");
      }
    });
  };

  const getSessionId = (key: string): Promise<string> => {
    const item = Office.context.mailbox.item;
    if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
      return Promise.reject("Not an appointment item");
    }
    const appCompose = item as Office.AppointmentCompose;
    return new Promise((resolve) => {
      appCompose.sessionData.getAsync(key, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          resolve(asyncResult.value as string);
        } else {
          resolve("");
        }
      });
    });
  };

  return {
    UpdateAttendees,
    AddResources,
    RemoveResources,
    UpdateRecipients,
    SetLocations,
    GetRecipients,
    InitializeAppointmentData,
    ExchangeResourceTypeEnum,
    SetAppointmentTime,
    saveSessionId,
    getSessionId,
  };
};
